import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { AsyncEntity } from 'src/global';
import createAsyncThunkFromFetch from '../../store/middlewares/createAsyncThunkFromFetch';
import { CompanyBanner } from 'src/features/companyBanners/companyBanners.types';
import {
  createCompanyBanners as createCompanyBannersAPI,
  deleteCompanyBanner as deleteCompanyBannerAPI,
  fetchCompanyBanner as fetchCompanyBannerAPI,
  getCompanyBanners,
  updateCompanyBanner as updateCompanyBannerAPI,
} from 'src/features/companyBanners/companyBanners.api';

const name = 'companyBanners';

const companyBannersEntityAdapter = createEntityAdapter<AsyncEntity<CompanyBanner>>({
  selectId: item => item.id,
});

export const companyBannersSelectors = companyBannersEntityAdapter.getSelectors();

export const fetchCompanyBanners = createAsyncThunkFromFetch(`${name}/fetchCompanyBanners`, getCompanyBanners);
export const createCompanyBanner = createAsyncThunkFromFetch(`${name}/createCompanyBanner`, createCompanyBannersAPI);
export const fetchCompanyBanner = createAsyncThunkFromFetch(`${name}/fetchCompanyBanner`, fetchCompanyBannerAPI);
export const updateCompanyBanner = createAsyncThunkFromFetch(`${name}/updateCompanyBanner`, updateCompanyBannerAPI);
export const deleteCompanyBanner = createAsyncThunkFromFetch(`${name}/deleteCompanyBanner`, deleteCompanyBannerAPI);

const packagesSlice = createSlice({
  name,
  initialState: companyBannersEntityAdapter.getInitialState<{
    isPending: boolean;
    hasError: boolean;
    companySelector: Record<string, string[]>;
  }>({
    isPending: false,
    hasError: false,
    companySelector: {},
  }),
  reducers: {},
  extraReducers: builder => {
    builder.addCase(fetchCompanyBanners.pending, state => {
      state.isPending = true;
      state.hasError = false;
    });
    builder.addCase(fetchCompanyBanners.fulfilled, (state, { payload }) => {
      companyBannersEntityAdapter.setAll(state, payload.items);
      state.isPending = false;
      state.hasError = false;
    });
    builder.addCase(fetchCompanyBanners.rejected, state => {
      state.isPending = false;
      state.hasError = true;
    });
    builder.addCase(deleteCompanyBanner.pending, (state, { meta }) => {
      companyBannersEntityAdapter.updateOne(state, {
        id: meta.arg.bannerId,
        changes: { isPending: true },
      });
    });
    builder.addCase(deleteCompanyBanner.fulfilled, (state, { meta }) => {
      companyBannersEntityAdapter.removeOne(state, meta.arg.bannerId);
    });
    builder.addCase(deleteCompanyBanner.rejected, (state, { meta }) => {
      companyBannersEntityAdapter.updateOne(state, {
        id: meta.arg.bannerId,
        changes: { isPending: false, hasError: true },
      });
    });
    builder.addCase(createCompanyBanner.fulfilled, (state, { payload }) => {
      companyBannersEntityAdapter.addOne(state, payload);
    });
    builder.addCase(fetchCompanyBanner.fulfilled, (state, { payload }) => {
      companyBannersEntityAdapter.upsertOne(state, payload);
    });
    builder.addCase(updateCompanyBanner.pending, state => {
      state.isPending = true;
      state.hasError = false;
    });
    builder.addCase(updateCompanyBanner.fulfilled, (state, { payload }) => {
      companyBannersEntityAdapter.updateOne(state, {
        id: payload.id,
        changes: payload,
      });
      state.isPending = false;
    });
  },
});

const { reducer } = packagesSlice;

export default reducer;
